/*
 * article-enhancements.js
 * - Injects a reading-progress bar at the top of the page
 * - Injects a reading-time badge near the article title
 * - Generates and injects a sticky TOC sidebar from the article's h2s
 *   (collapsible accordion on mobile)
 * - Wires the copy-link share button
 *
 * Reads config from data-* attributes on the <article-enhancements> custom
 * element. The Liquid section places that element on the page; this script
 * walks the existing Craft .article-template DOM and bolts features onto it.
 */
(function () {
  if (window.__articleEnhancementsWired) return;
  window.__articleEnhancementsWired = true;

  function init() {
    var host = document.querySelector('article-enhancements');
    if (!host) return;

    var content = document.querySelector('.article-template__content');
    if (!content) return;

    var showToc = host.dataset.showToc === 'true';
    var showProgress = host.dataset.showProgress === 'true';
    var showReadingTime = host.dataset.showReadingTime === 'true';
    var tocHeading = host.dataset.tocHeading || 'In this article';
    var readingTime = parseInt(host.dataset.readingTime, 10) || 1;

    if (showProgress) injectProgressBar();
    if (showReadingTime) injectReadingTimeBadge(readingTime);
    if (showToc) injectTOC(content, tocHeading);
    wireCopyLink();
  }

  /* ---- 1. Reading progress bar ---- */
  function injectProgressBar() {
    var bar = document.createElement('div');
    bar.className = 'article-enhancements__progress';
    bar.innerHTML = '<div class="article-enhancements__progress-fill" data-progress-fill></div>';
    document.body.appendChild(bar);

    var fill = bar.querySelector('[data-progress-fill]');
    var article = document.querySelector('.article-template__content');
    if (!article) return;

    var ticking = false;
    function update() {
      ticking = false;
      var rect = article.getBoundingClientRect();
      var viewportH = window.innerHeight;
      var articleH = rect.height;
      // How far into the article we've scrolled (0 to 1).
      var scrolled = -rect.top;
      var maxScroll = articleH - viewportH;
      if (maxScroll <= 0) {
        fill.style.transform = 'scaleX(0)';
        return;
      }
      var progress = Math.max(0, Math.min(1, scrolled / maxScroll));
      fill.style.transform = 'scaleX(' + progress + ')';
    }
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    update();
  }

  /* ---- 2. Reading-time badge ---- */
  function injectReadingTimeBadge(minutes) {
    var titleArea = document.querySelector('.article-template__title');
    if (!titleArea) return;
    var existing = document.querySelector('.article-enhancements__reading-time');
    if (existing) return;
    var badge = document.createElement('div');
    badge.className = 'article-enhancements__reading-time';
    badge.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14">' +
      '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>' +
      '</svg>' +
      '<span>' + minutes + ' min read</span>';
    titleArea.parentNode.insertBefore(badge, titleArea.nextSibling);
  }

  /* ---- 3. TOC sidebar / mobile accordion ---- */
  function slugify(text) {
    return text.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 60);
  }

  function injectTOC(content, headingText) {
    var headings = content.querySelectorAll('h2');
    if (headings.length < 2) return; // not worth a TOC for 0 or 1 heading

    // Ensure each heading has an id we can link to.
    var used = {};
    headings.forEach(function (h) {
      if (!h.id) {
        var slug = slugify(h.textContent);
        if (used[slug]) {
          slug = slug + '-' + (used[slug] + 1);
        }
        used[h.id || slug] = (used[h.id || slug] || 0) + 1;
        h.id = slug;
      }
    });

    var nav = document.createElement('nav');
    nav.className = 'article-enhancements__toc';
    nav.setAttribute('aria-label', 'Table of contents');

    var label = document.createElement('button');
    label.type = 'button';
    label.className = 'article-enhancements__toc-toggle';
    label.setAttribute('aria-expanded', 'false');
    label.innerHTML =
      '<span class="article-enhancements__toc-heading">' + headingText + '</span>' +
      '<span class="article-enhancements__toc-chevron" aria-hidden="true">' +
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><polyline points="6 9 12 15 18 9"/></svg>' +
      '</span>';
    nav.appendChild(label);

    var list = document.createElement('ol');
    list.className = 'article-enhancements__toc-list';
    headings.forEach(function (h) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.className = 'article-enhancements__toc-link';
      a.textContent = h.textContent;
      a.dataset.target = h.id;
      li.appendChild(a);
      list.appendChild(li);
    });
    nav.appendChild(list);

    // Mobile: clicking the label toggles open/closed
    label.addEventListener('click', function () {
      var open = nav.classList.toggle('article-enhancements__toc--open');
      label.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    // Click a link -> close mobile drawer
    list.addEventListener('click', function (e) {
      if (e.target.matches('.article-enhancements__toc-link')) {
        nav.classList.remove('article-enhancements__toc--open');
        label.setAttribute('aria-expanded', 'false');
      }
    });

    // Insert the TOC right before the article content
    content.parentNode.insertBefore(nav, content);

    // Highlight the section the user is currently reading
    if ('IntersectionObserver' in window) {
      var links = list.querySelectorAll('.article-enhancements__toc-link');
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var id = entry.target.id;
          var link = list.querySelector('[data-target="' + CSS.escape(id) + '"]');
          if (!link) return;
          if (entry.isIntersecting) {
            links.forEach(function (l) { l.classList.remove('article-enhancements__toc-link--active'); });
            link.classList.add('article-enhancements__toc-link--active');
          }
        });
      }, { rootMargin: '-40% 0% -55% 0%', threshold: 0 });
      headings.forEach(function (h) { io.observe(h); });
    }
  }

  /* ---- 4. Copy-link share button ---- */
  function wireCopyLink() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-copy-link]');
      if (!btn) return;
      var url = btn.dataset.copyUrl || window.location.href;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function () {
          flashCopied(btn);
        });
      } else {
        // Fallback: temp textarea
        var ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); flashCopied(btn); } catch (_) {}
        document.body.removeChild(ta);
      }
    });
  }
  function flashCopied(btn) {
    btn.classList.add('article-enhancements__share-btn--copied');
    setTimeout(function () { btn.classList.remove('article-enhancements__share-btn--copied'); }, 1600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
